"use client";

import { AnalyticsClient } from "@lib/analytics/AnalyticsClient";
import { useBreakpoint } from "@ecco/ui/use-breakpoint";
import { useInView } from "@ecco/ui/use-in-view";
import { useEffect, useMemo, useRef, useState } from "react";
import type { HeroProps } from "./Hero.types";
import { getHeroPosterUrl } from "./heroVideo";

const HERO_VIDEO_PLAY_VISIBILITY_RATIO = 0.25;
const HERO_VIDEO_PAUSE_VISIBILITY_RATIO = 0.1;
const HERO_PROMOTION_ANALYTICS_DELAY_MS = 2000;

export type HeroDesktopBreakpoint = "sm" | "md";

type UseHeroModuleOptions = HeroProps & {
    respectReducedMotion?: boolean;
    desktopBreakpoint?: HeroDesktopBreakpoint;
};

export type HeroModuleState = {
    isMobile: boolean;
    isAboveTheFold: boolean;
    shouldRenderVideo: boolean;
    shouldRenderImage: boolean;
    hasDesktopImage: boolean;
    hasMobileImage: boolean;
    desktopImageUrl?: string;
    desktopImageAlt?: string;
    mobileImageUrl?: string;
    mobileImageAlt?: string;
    shouldRenderContent: boolean;
    shouldShowControls: boolean;
    currentVideoId: string;
    promotionImage?: string;
    posterUrl?: string;
    isVideoMuted: boolean;
    setIsVideoMuted: (muted: boolean) => void;
    videoPlayerMuted: boolean;
    setVideoPlaying: (playing: boolean, fromUser?: boolean) => void;
    toggleVideoPlayback: () => void;
    onPlayerPlayingChange: (playing: boolean) => void;
    onPlayerEnded: () => void;
    isPlaybackConfirmed: boolean;
    isPosterVisible: boolean;
    shouldPlayVideo: boolean;
    /** False for inactive carousel slides — avoid mounting multiple Wistia players. */
    shouldMountVideo: boolean;
    videoRef: ReturnType<typeof useInView>["ref"];
    promotionRef: ReturnType<typeof useInView>["ref"];
};

export function useHeroModule({
    id,
    title,
    description,
    legalText,
    callToActions,
    image,
    mobileImage,
    videoId: hashedId,
    mobileVideoId: mobileHashedId,
    videoThumbnailUrl,
    autoplay = true,
    muted = true,
    videoControls = true,
    index,
    slideIndex = 0,
    isCarousel = false,
    isVideoPlaying: isVideoPlayingProp = true,
    setIsVideoPlaying,
    activeSlide,
    respectReducedMotion = false,
    desktopBreakpoint = "sm",
}: UseHeroModuleOptions): HeroModuleState {
    const [isVideoMuted, setIsVideoMutedState] = useState(muted);
    const [isVideoPlayingLocal, setIsVideoPlayingLocal] = useState(autoplay);
    const [isPlaybackConfirmed, setIsPlaybackConfirmed] = useState(false);
    const [hasStartedPlayback, setHasStartedPlayback] = useState(false);
    const isPlaybackConfirmedRef = useRef(false);
    const isDesktop = useBreakpoint(desktopBreakpoint);
    const isMobile = !isDesktop;
    const [currentVideoId, setCurrentVideoId] = useState(() => hashedId ?? mobileHashedId ?? "");
    const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
    const isVisibleEnoughRef = useRef(autoplay);
    const hasMeasuredVisibilityRef = useRef(false);
    const promotionAnalyticsTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
        undefined
    );
    const playbackRef = useRef({
        shouldRenderVideo: false,
        isCarouselPlayback: false,
        activeSlide,
        slideIndex,
        autoplay,
        reducedMotionEnabled: false,
        userPaused: false,
        setVideoPlaying: (_playing: boolean, _fromUser?: boolean) => {},
    });
    const onPlaybackIntersectionRef = useRef<(entry: IntersectionObserverEntry) => void>(() => {});

    const reducedMotionEnabled = respectReducedMotion && prefersReducedMotion;
    const hasDesktopVideo = Boolean(hashedId);
    const hasMobileVideo = Boolean(mobileHashedId);
    const hasDesktopImage = Boolean(image);
    const hasMobileImage = Boolean(mobileImage);
    const hasConfiguredImage = hasDesktopImage || hasMobileImage;
    const isAboveTheFold = isCarousel ? slideIndex === 0 && index === 0 : index === 0;
    const shouldRenderImage = hasConfiguredImage;
    const shouldRenderVideo =
        !reducedMotionEnabled &&
        !hasConfiguredImage &&
        (isMobile ? hasMobileVideo || hasDesktopVideo : hasDesktopVideo);
    const shouldShowControls = shouldRenderVideo && videoControls;
    const shouldRenderContent = Boolean(title || description || callToActions?.length || legalText);

    const promotionImage = image?.url ?? videoThumbnailUrl;

    const setIsVideoMuted = (nextMuted: boolean) => {
        setIsVideoMutedState(nextMuted);
    };

    const videoPlayerMuted = isVideoMuted;

    const setVideoPlaying = (playing: boolean, fromUser = false) => {
        if (fromUser) {
            playbackRef.current.userPaused = !playing;
        }

        if (!playing) {
            if (isPlaybackConfirmedRef.current) {
                isPlaybackConfirmedRef.current = false;
                setIsPlaybackConfirmed(false);
            }
        }

        const intentAlreadyPlaying = setIsVideoPlaying ? isVideoPlayingProp : isVideoPlayingLocal;

        if (fromUser && playing && intentAlreadyPlaying && !isPlaybackConfirmedRef.current) {
            setIsVideoPlayingLocal(false);
            setIsVideoPlaying?.(false);
            queueMicrotask(() => {
                setIsVideoPlayingLocal(true);
                setIsVideoPlaying?.(true);
            });
            return;
        }

        if (playing === intentAlreadyPlaying) {
            return;
        }

        setIsVideoPlayingLocal(playing);
        setIsVideoPlaying?.(playing);
    };

    const effectiveIsVideoPlaying = setIsVideoPlaying ? isVideoPlayingProp : isVideoPlayingLocal;
    const isActiveSlide = activeSlide === undefined || activeSlide === slideIndex;
    const shouldPlayVideo = effectiveIsVideoPlaying && isActiveSlide;
    const shouldMountVideo = shouldRenderVideo && isActiveSlide;
    const isPosterVisible = !hasStartedPlayback || !isActiveSlide;

    const toggleVideoPlayback = () => {
        setVideoPlaying(!isPlaybackConfirmed, true);
    };

    const onPlayerPlayingChange = (playing: boolean) => {
        if (!isActiveSlide) {
            return;
        }

        if (playing) {
            if (!isPlaybackConfirmedRef.current) {
                isPlaybackConfirmedRef.current = true;
                setIsPlaybackConfirmed(true);
                setHasStartedPlayback(true);
            }
            if (!playbackRef.current.userPaused) {
                setVideoPlaying(true);
            }
            return;
        }

        const { userPaused, autoplay: shouldAutoplay, reducedMotionEnabled } = playbackRef.current;
        const shouldKeepAutoplayIntent = !userPaused && shouldAutoplay && !reducedMotionEnabled;

        if (shouldKeepAutoplayIntent) {
            return;
        }

        if (isPlaybackConfirmedRef.current) {
            isPlaybackConfirmedRef.current = false;
            setIsPlaybackConfirmed(false);
        }

        setVideoPlaying(false);
    };

    const onPlayerEnded = () => {
        if (!isActiveSlide) {
            return;
        }

        isPlaybackConfirmedRef.current = false;
        setIsPlaybackConfirmed(false);
        setVideoPlaying(false);
    };

    const resetPlaybackUi = () => {
        isPlaybackConfirmedRef.current = false;
        setIsPlaybackConfirmed(false);
        setHasStartedPlayback(false);
    };

    const isCarouselPlayback = Boolean(setIsVideoPlaying);

    playbackRef.current = {
        shouldRenderVideo,
        isCarouselPlayback,
        activeSlide,
        slideIndex,
        autoplay,
        reducedMotionEnabled,
        userPaused: playbackRef.current.userPaused,
        setVideoPlaying,
    };

    onPlaybackIntersectionRef.current = (entry) => {
        const ctx = playbackRef.current;

        if (ctx.isCarouselPlayback) {
            if (ctx.activeSlide !== undefined && ctx.activeSlide !== ctx.slideIndex) {
                return;
            }
        } else if (!ctx.shouldRenderVideo) {
            return;
        }

        const ratio = entry.intersectionRatio;

        if (!hasMeasuredVisibilityRef.current) {
            hasMeasuredVisibilityRef.current = true;

            if (ratio < HERO_VIDEO_PLAY_VISIBILITY_RATIO) {
                isVisibleEnoughRef.current = false;
                return;
            }
        }

        if (ratio < HERO_VIDEO_PLAY_VISIBILITY_RATIO && ratio > HERO_VIDEO_PAUSE_VISIBILITY_RATIO) {
            return;
        }

        const visibleEnough = ratio >= HERO_VIDEO_PLAY_VISIBILITY_RATIO;

        if (visibleEnough === isVisibleEnoughRef.current) {
            return;
        }

        isVisibleEnoughRef.current = visibleEnough;

        if (!visibleEnough) {
            ctx.setVideoPlaying(false);
            return;
        }

        if (!ctx.userPaused && ctx.autoplay && !ctx.reducedMotionEnabled) {
            ctx.setVideoPlaying(true);
        }
    };

    useEffect(() => {
        if (!isActiveSlide) {
            resetPlaybackUi();
        }
    }, [isActiveSlide]);

    useEffect(() => {
        if (!respectReducedMotion) {
            return;
        }

        const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
        const update = () => {
            setPrefersReducedMotion(mediaQuery.matches);

            if (mediaQuery.matches) {
                setVideoPlaying(false);
            }
        };

        update();
        mediaQuery.addEventListener("change", update);
        return () => mediaQuery.removeEventListener("change", update);
    }, [respectReducedMotion]);

    useEffect(() => {
        if (activeSlide === slideIndex && setIsVideoPlaying) {
            playbackRef.current.userPaused = false;
            setIsVideoPlaying(autoplay && !reducedMotionEnabled);
        }
    }, [autoplay, activeSlide, reducedMotionEnabled, setIsVideoPlaying, slideIndex]);

    useEffect(() => {
        const newVideoId = mobileHashedId ? (isMobile ? mobileHashedId : hashedId) : hashedId;
        if (newVideoId && newVideoId !== currentVideoId) {
            playbackRef.current.userPaused = false;
            setIsVideoMutedState(muted);
            resetPlaybackUi();
            setCurrentVideoId(newVideoId);
            setVideoPlaying(autoplay && !reducedMotionEnabled);
        }
    }, [autoplay, currentVideoId, hashedId, isMobile, mobileHashedId, muted, reducedMotionEnabled]);

    const { ref: videoRef } = useInView({
        threshold: [0, HERO_VIDEO_PAUSE_VISIBILITY_RATIO, HERO_VIDEO_PLAY_VISIBILITY_RATIO],
        onChange: (_inView, entry) => {
            if (!isCarouselPlayback) {
                onPlaybackIntersectionRef.current(entry);
            }
        },
    });

    const { ref: promotionRef } = useInView({
        threshold: isCarouselPlayback
            ? [0, HERO_VIDEO_PAUSE_VISIBILITY_RATIO, HERO_VIDEO_PLAY_VISIBILITY_RATIO, 0.5]
            : 0.5,
        onChange: (inView, entry) => {
            if (isCarouselPlayback) {
                onPlaybackIntersectionRef.current(entry);
            }

            if (promotionAnalyticsTimeoutRef.current) {
                clearTimeout(promotionAnalyticsTimeoutRef.current);
                promotionAnalyticsTimeoutRef.current = undefined;
            }

            if (inView && id) {
                promotionAnalyticsTimeoutRef.current = setTimeout(
                    () =>
                        AnalyticsClient.viewPromotion({
                            image: promotionImage,
                            title,
                            index: slideIndex,
                            id,
                        }),
                    HERO_PROMOTION_ANALYTICS_DELAY_MS
                );
            }
        },
    });

    useEffect(() => {
        return () => {
            if (promotionAnalyticsTimeoutRef.current) {
                clearTimeout(promotionAnalyticsTimeoutRef.current);
            }
        };
    }, []);

    const posterUrl = useMemo(() => {
        const activeHashedId = mobileHashedId ? (isMobile ? mobileHashedId : hashedId) : hashedId;

        return getHeroPosterUrl({
            hashedId: activeHashedId,
            thumbnailUrl: videoThumbnailUrl,
            imageUrl: image?.url,
            imageMobileUrl: mobileImage?.url,
            isMobile,
        });
    }, [hashedId, image, isMobile, mobileHashedId, mobileImage, videoThumbnailUrl]);

    return {
        isMobile,
        isAboveTheFold,
        shouldRenderVideo,
        shouldRenderImage,
        hasDesktopImage,
        hasMobileImage,
        desktopImageUrl: image?.url,
        desktopImageAlt: image?.alt,
        mobileImageUrl: mobileImage?.url,
        mobileImageAlt: mobileImage?.alt,
        shouldRenderContent,
        shouldShowControls,
        currentVideoId,
        promotionImage,
        posterUrl,
        isVideoMuted,
        setIsVideoMuted,
        videoPlayerMuted,
        setVideoPlaying,
        toggleVideoPlayback,
        onPlayerPlayingChange,
        onPlayerEnded,
        isPlaybackConfirmed,
        isPosterVisible,
        shouldPlayVideo,
        shouldMountVideo,
        videoRef,
        promotionRef,
    };
}
